import { defineStore } from 'pinia'
import { api } from '@/services/api.js'
import { isNotificationsV2Enabled } from '@/services/pushDevice.js'
import { applyAppBadge, mergeInboxItems, pendingAcknowledgements, withItemRead } from '@/services/inbox.js'

/**
 * The staff inbox (GLOSSARY "Inbox", #141), behind notifications-v2 for this
 * build's tenant: the notifications sent to the signed-in staff member in the
 * last 30 days, read from the server (GET /api/notifications) so it is the
 * same on every device. It replaces the localStorage message history
 * (stores/notifications.js) for flag-on tenants; flag-off tenants never load it.
 *
 * Items (as the API returns them, times in ms): { id, type, tenant, title, body,
 * link, createdAt, readAt, acknowledgement, acknowledgedAt, acknowledgedVia,
 * canAcknowledge, closedAt, closedBy, closedByName, recipientCount,
 * acknowledgedCount }.
 *
 * Acknowledgement (#142): `awaiting` is the server's list of what awaits this
 * staff member's acknowledgement (every page), refreshed with the inbox (app
 * open, login, push arrival, back in the foreground), so the sticky banner
 * goes on every device once a notification is closed for them; `pending` is
 * what the banner shows. acknowledge() posts it and replaces the item.
 *
 * The unread count drives the header bell and the home screen app badge,
 * set whenever the count changes (load, arrival, read, logout).
 */
const NO_STORE = { headers: { 'Cache-Control': 'no-store' } }

export const useInboxStore = defineStore('inbox', {
    state: () => ({
        enabled: false,
        items: [],
        awaiting: [],
        acknowledging: null,
        unread: 0,
        hasMore: false,
        loading: false,
        loaded: false,
        error: null
    }),
    getters: {
        /** The banner's notifications, oldest first. */
        pending: state => pendingAcknowledgements(state.awaiting, state.items)
    },
    actions: {
        setUnread(count) {
            this.unread = Math.max(0, Number(count) || 0)
            applyAppBadge(this.unread)
        },

        /** Load (or reload) the newest page and the unread count. On app open, after login, on arrival. */
        async refresh() {
            this.enabled = await isNotificationsV2Enabled()
            if (!this.enabled) return
            this.loading = true
            try {
                const page = await api.get('/api/notifications', NO_STORE)
                this.items = mergeInboxItems(this.loaded ? this.items : [], page?.items || [])
                if (!this.loaded) this.hasMore = !!page?.hasMore
                this.awaiting = Array.isArray(page?.awaitingAcknowledgement) ? page.awaitingAcknowledgement : []
                this.loaded = true
                this.error = null
                this.setUnread(page?.unread)
            } catch (e) {
                // Keep what is shown; the next open or arrival tries again
                this.error = e?.message || 'Could not load the inbox'
            } finally {
                this.loading = false
            }
        },

        /** Only the count, for the bell when the app comes back to the foreground. */
        async refreshUnread() {
            if (!this.enabled) return
            try {
                const res = await api.get('/api/notifications/unread-count', NO_STORE)
                this.setUnread(res?.unread)
            } catch {
                // Keep the last count
            }
        },

        /** The next page of older notifications. */
        async loadMore() {
            if (!this.enabled || !this.hasMore || this.loading) return
            const last = this.items[this.items.length - 1]
            if (!last) return
            this.loading = true
            try {
                const page = await api.get(`/api/notifications?before=${last.id}`, NO_STORE)
                this.items = mergeInboxItems(this.items, page?.items || [])
                this.hasMore = !!page?.hasMore
                this.setUnread(page?.unread)
            } catch (e) {
                this.error = e?.message || 'Could not load the inbox'
            } finally {
                this.loading = false
            }
        },

        /**
         * Open one notification: fetch it if it is not loaded (a tap from a cold
         * start), then mark it read and, given the tapped push's delivery, record
         * "opened" on that delivery. Opening one already read posts nothing.
         * @returns {Promise<object|null>} the item, or null when it is not theirs or has gone
         */
        async open(notificationId, { deliveryId = null } = {}) {
            let found = this.items.find(n => n.id === notificationId)
            if (!found) {
                try {
                    const res = await api.get(`/api/notifications/${notificationId}`, NO_STORE)
                    found = res?.item || null
                } catch {
                    found = null
                }
                if (!found) return null
                this.items = mergeInboxItems(this.items, [found])
            }
            if (!found.readAt || deliveryId) {
                this.items = withItemRead(this.items, notificationId, Date.now())
                try {
                    const res = await api.post(`/api/notifications/${notificationId}/read`, deliveryId ? { deliveryId } : {})
                    this.setUnread(res?.unread)
                } catch {
                    // Shown as read here; the next refresh brings the server's answer
                }
            }
            return this.items.find(n => n.id === notificationId) || found
        },

        async markAllRead() {
            if (!this.enabled) return
            const now = Date.now()
            this.items = this.items.map(n => (n.readAt ? n : { ...n, readAt: now }))
            try {
                const res = await api.post('/api/notifications/read-all', {})
                this.setUnread(res?.unread)
            } catch {
                await this.refresh()
            }
        },

        /** A push arrived while the app is open (service worker message): show its count now, then reload. */
        arrived({ unread = null } = {}) {
            if (Number.isInteger(unread)) this.setUnread(unread)
            return this.refresh()
        },

        /** Replace one item with the server's latest copy (after acknowledging, #142). */
        replaceItem(updated) {
            if (!updated?.id) return
            this.items = mergeInboxItems(this.items, [updated])
            if (this.awaiting.some(n => n.id === updated.id)) {
                this.awaiting = this.awaiting.map(n => (n.id === updated.id ? updated : n))
            }
        },

        /**
         * Acknowledge one notification (#142), from the inbox item or the banner.
         * A 404 or 409 (not theirs, asks none) reloads, as the copy here is out of date.
         * @param {'inbox'|'push'} [via]
         * @returns {Promise<boolean>} whether it was acknowledged
         */
        async acknowledge(notificationId, { via = 'inbox' } = {}) {
            if (this.acknowledging) return false
            this.acknowledging = notificationId
            try {
                const res = await api.post(`/api/notifications/${notificationId}/acknowledge`, { via })
                if (res?.item) this.replaceItem(res.item)
                else await this.refresh()
                return !!res?.acknowledged
            } catch (e) {
                this.error = e?.message || 'Could not acknowledge'
                await this.refresh()
                return false
            } finally {
                this.acknowledging = null
            }
        },

        /** Logout: the next staff member gets their own inbox; the badge goes. */
        reset() {
            this.items = []
            this.awaiting = []
            this.hasMore = false
            this.loaded = false
            this.error = null
            this.enabled = false
            this.setUnread(0)
        }
    }
})
